import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit.svg";
import { startEditQuickRequest, startEditQuoteRequest, startEditConsolidatedRequest } from "api";
import errorToast from "helpers/errorToast";
import { classNames } from "helpers/commonHelpers";
import testIds from "constants/testIds";

interface IEditTabProps {
  isQuickRequest: boolean;
  shipmentId: string;
  isShipment: boolean;
  quotationsAvailable: boolean;
  isConsolidated?: boolean;
  isQuoteRequestBooked?: boolean;
  onEditClick?: () => void;
}

function EditTab({
  isQuickRequest,
  shipmentId,
  isShipment,
  quotationsAvailable,
  isConsolidated,
  isQuoteRequestBooked = false,
  onEditClick,
}: Readonly<IEditTabProps>) {
  const navigate = useNavigate();

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
      return;
    }

    // Choose the correct function based on the request type
    let getDraftReferenceFn = startEditQuoteRequest;
    if (isQuickRequest) getDraftReferenceFn = startEditQuickRequest;
    else if (isConsolidated) getDraftReferenceFn = startEditConsolidatedRequest;

    getDraftReferenceFn(shipmentId)
      .then((res: any) => {
        const draftId = res.data.draft_reference;

        const urls = {
          quickRequest: `/quick-request/${shipmentId}/edit?draft_id=${draftId}`,
          shipment: `/shipment/${shipmentId}/edit?draft_id=${draftId}`,
          consolidated: `/order-consolidation/${shipmentId}/edit?active_step=shipment-details&draft_id=${draftId}`,
          quote: `/quote-request/${shipmentId}/edit?draft_id=${draftId}&quotesAvailable=${quotationsAvailable}`,
        };

        if (isQuickRequest) navigate(urls.quickRequest);
        else if (isShipment) navigate(urls.shipment);
        else if (isConsolidated) navigate(urls.consolidated);
        else navigate(urls.quote);
      })
      .catch((err) => errorToast(err));
  };

  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 0 }}
      overlay={
        <Tooltip id="button-tooltip">
          {isShipment && <FormattedMessage id="new-request.form.editShipment" defaultMessage="Edit shipment" />}
          {!isShipment &&
            (isQuickRequest ? (
              <FormattedMessage id="new-request.form.editQuickRequest" defaultMessage="Edit quick request" />
            ) : (
              <FormattedMessage id="new-request.form.editQuoteRequest" defaultMessage="Edit quote request" />
            ))}
        </Tooltip>
      }
    >
      <button
        type="button"
        className={classNames("topnav-v2__action-btn topnav-v2__edit-btn", {
          booked: isQuoteRequestBooked,
        })}
        onClick={handleEditClick}
        aria-label="Edit"
        data-testid={testIds.SHIPMENT_DASHBOARD_EDIT_BUTTON}
      >
        <EditIcon />
      </button>
    </OverlayTrigger>
  );
}

EditTab.propTypes = {
  isQuickRequest: PropTypes.bool.isRequired,
  shipmentId: PropTypes.string.isRequired,
  isShipment: PropTypes.bool.isRequired,
  quotationsAvailable: PropTypes.bool.isRequired,
  isConsolidated: PropTypes.bool,
  onEditClick: PropTypes.func,
};

EditTab.defaultProps = {
  isConsolidated: false,
  onEditClick: undefined,
};

export default EditTab;
